import { useCallback, useEffect, useMemo, useState } from "react";

const STORAGE_PREFIX = "table-columns:";

const readStored = (storageKey) => {
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return null;
        return parsed;
    } catch {
        return null;
    }
};

const buildIndex = (columns) =>
    columns.reduce((acc, col) => {
        acc[col.id] = col;
        return acc;
    }, {});

/**
 * Restores stored ordering while keeping pinned columns at their declared positions.
 * Unknown ids are dropped, and missing ids use their declared semantic position.
 */
const sameOrder = (left, right) =>
    Array.isArray(left) &&
    Array.isArray(right) &&
    left.length === right.length &&
    left.every((id, index) => id === right[index]);

const normalize = (stored, columns, byId, legacyDefaultOrders = []) => {
    const declaredOrder = columns.map((col) => col.id);
    const pinnedPositions = new Map();
    columns.forEach((col, index) => {
        if (col.pinned) pinnedPositions.set(col.id, index);
    });

    const storedOrder = Array.isArray(stored?.order) ? stored.order : [];
    const filtered = storedOrder.filter((id) => byId[id] && !byId[id].pinned);
    const isLegacyDefault = legacyDefaultOrders.some((order) => sameOrder(storedOrder, order));

    // Migrate an old default order. Keep a custom operator order unchanged.
    const orderToNormalize = isLegacyDefault ? [] : filtered;

    // A column added to COLUMNS after a user already has a stored config is
    // slotted in at its DECLARED position — right after whichever declared
    // neighbour that user already has — rather than appended to the end.
    //
    // Appending was the old behaviour and it quietly defeats the point of
    // adding a column: these tables scroll horizontally, so "last" means
    // "off-screen until you go looking", and every existing admin would see a
    // new column there no matter where it was declared. Their own reordering is
    // still preserved; this only decides where the ids they have never seen
    // land relative to the ones they have.
    const declaredMovable = declaredOrder.filter((id) => !byId[id].pinned);
    const movableOrder = [...orderToNormalize];

    declaredMovable.forEach((id, declaredIndex) => {
        if (movableOrder.includes(id)) return;

        let insertAt = 0;
        for (let i = declaredIndex - 1; i >= 0; i -= 1) {
            const position = movableOrder.indexOf(declaredMovable[i]);
            if (position !== -1) {
                insertAt = position + 1;
                break;
            }
        }
        movableOrder.splice(insertAt, 0, id);
    });

    const finalOrder = [];
    let movableIdx = 0;
    declaredOrder.forEach((_, position) => {
        const pinnedAtPosition = [...pinnedPositions.entries()].find(([, p]) => p === position);
        if (pinnedAtPosition) {
            finalOrder.push(pinnedAtPosition[0]);
        } else if (movableIdx < movableOrder.length) {
            finalOrder.push(movableOrder[movableIdx]);
            movableIdx += 1;
        }
    });
    while (movableIdx < movableOrder.length) {
        finalOrder.push(movableOrder[movableIdx]);
        movableIdx += 1;
    }

    const storedHidden = Array.isArray(stored?.hidden) ? stored.hidden : [];
    const hidden = storedHidden.filter((id) => byId[id] && !byId[id].pinned);

    return { order: finalOrder, hidden };
};

/**
 * Persisted visibility + ordering for a table's columns, keyed by `tableId`.
 * Columns with `pinned: true` are locked at their declared position and always visible.
 */
const useTableColumnsConfig = (tableId, columns, options = {}) => {
    const storageKey = `${STORAGE_PREFIX}${tableId}`;
    const byId = useMemo(() => buildIndex(columns), [columns]);

    const [state, setState] = useState(() =>
        normalize(readStored(storageKey), columns, byId, options.legacyDefaultOrders),
    );

    useEffect(() => {
        try {
            localStorage.setItem(storageKey, JSON.stringify(state));
        } catch {
            // storage may be unavailable (private mode, quota); silently ignore
        }
    }, [state, storageKey]);

    const orderedColumns = useMemo(
        () => state.order.map((id) => byId[id]).filter(Boolean),
        [state.order, byId],
    );

    const hiddenSet = useMemo(() => new Set(state.hidden), [state.hidden]);

    const visibleColumns = useMemo(
        () => orderedColumns.filter((col) => !hiddenSet.has(col.id)),
        [orderedColumns, hiddenSet],
    );

    const toggleColumn = useCallback(
        (id) => {
            if (byId[id]?.pinned) return;
            setState((prev) => {
                const isHidden = prev.hidden.includes(id);
                return {
                    ...prev,
                    hidden: isHidden ? prev.hidden.filter((x) => x !== id) : [...prev.hidden, id],
                };
            });
        },
        [byId],
    );

    const moveColumn = useCallback(
        (id, direction) => {
            if (byId[id]?.pinned) return;
            setState((prev) => {
                const index = prev.order.indexOf(id);
                if (index === -1) return prev;
                const target = direction === "up" ? index - 1 : index + 1;
                if (target < 0 || target >= prev.order.length) return prev;
                if (byId[prev.order[target]]?.pinned) return prev;
                const next = [...prev.order];
                [next[index], next[target]] = [next[target], next[index]];
                return { ...prev, order: next };
            });
        },
        [byId],
    );

    const reset = useCallback(() => {
        setState({
            order: columns.map((col) => col.id),
            hidden: [],
        });
    }, [columns]);

    return {
        orderedColumns,
        visibleColumns,
        hiddenSet,
        toggleColumn,
        moveColumn,
        reset,
    };
};

export default useTableColumnsConfig;
